import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { MCPDebugLogger } from './debug';

// Exponential backoff retry function
const retryWithBackoff = async (
  fn: () => Promise<AxiosResponse>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<AxiosResponse> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isRateLimited = error.response?.status === 429;
      const isLastAttempt = attempt === maxRetries;
      
      if (!isRateLimited || isLastAttempt) {
        throw error;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.warn(`Rate limited, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
};

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api',
  timeout: 30000,
  withCredentials: true, // Include cookies for authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor with rate limit handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // Handle rate limiting with retry
    if (error.response?.status === 429 && originalRequest && !(originalRequest as any)._retry) {
      (originalRequest as any)._retry = true;
      
      try {
        // Use exponential backoff for rate limited requests
        return await retryWithBackoff(() => axios.request(originalRequest));
      } catch (retryError) {
        console.error('Rate limit retry failed:', retryError);
        // Fall through to normal error handling
      }
    }
    
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Only redirect to login if this is not an auth check request
      // This prevents infinite loops when checking auth status
      const isAuthCheck = originalRequest?.url?.includes('/auth/me');
      if (!isAuthCheck) {
        // Clear token and redirect to home
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: {
    message: string;
    stack?: string;
  };
}

// Auth-specific response types that include user directly
export interface AuthResponse {
  success: boolean;
  message?: string;
  user?: any;
  token?: string;
  error?: {
    message: string;
    stack?: string;
  };
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<ApiResponse<{ token: string; user: any }>>('/auth/login', { email, password }),
  
  register: (email: string, password: string, name?: string) =>
    api.post<ApiResponse<{ token: string; user: any }>>('/auth/register', { email, password, name }),
  
  requestOtp: (email: string) =>
    api.post<ApiResponse>('/auth/request-otp', { email }),
  
  verifyOtp: (email: string, code: string) =>
    api.post<AuthResponse>('/auth/verify-otp', { email, code }),
  
  logout: () =>
    api.post<ApiResponse>('/auth/logout'),
  
  me: () =>
    api.get<AuthResponse>('/auth/me'),
  
  getMe: () =>
    api.get<AuthResponse>('/auth/me'),
  
  refreshToken: () =>
    api.post<ApiResponse<{ token: string }>>('/auth/refresh'),
  
  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post<ApiResponse>('/auth/reset-password', { token, password }),
};

// Users API
export const usersApi = {
  getProfile: () =>
    api.get<ApiResponse<{ user: any }>>('/users/profile'),
  
  updateProfile: (data: { name?: string; profilePicture?: string; marketingOptIn?: boolean }) =>
    api.patch<ApiResponse<{ user: any }>>('/users/profile', data),

  uploadProfilePicture: (file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    return api.post<ApiResponse<{ profilePicture: string; filename: string }>>('/users/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  cropProfilePicture: (data: { filename: string; x: number; y: number; width: number; height: number }) =>
    api.post<ApiResponse<{ profilePicture: string; filename: string }>>('/users/profile/picture/crop', data),

  // User management endpoints
  list: () =>
    api.get<PaginatedResponse<any>>('/users'),
  
  create: (data: {
    email: string;
    name?: string;
    role: 'buyer' | 'seller';
    accountRole: 'user' | 'admin';
    permissions?: string[];
  }) =>
    api.post<ApiResponse<{ user: any }>>('/users', data),
  
  update: (id: string, data: {
    name?: string;
    role?: 'buyer' | 'seller';
    accountRole?: 'user' | 'admin';
    permissions?: string[];
  }) =>
    api.put<ApiResponse<{ user: any }>>(`/users/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/users/${id}`),

  // Settings endpoints
  getSettings: () =>
    api.get<ApiResponse<{
      customPrompt: string;
      multipleItemsPerPhoto: boolean;
      detectionModel: 'lite' | 'mobilenet' | 'none';
      detectionConfidence: number;
      maxDetections: number;
      showRealtimeBoxes: boolean;
    }>>('/users/settings'),

  updateSettings: (settings: {
    customPrompt?: string;
    multipleItemsPerPhoto?: boolean;
    detectionModel?: 'lite' | 'mobilenet' | 'none';
    detectionConfidence?: number;
    maxDetections?: number;
    showRealtimeBoxes?: boolean;
  }) =>
    api.put<ApiResponse<any>>('/users/settings', settings),
};

// Projects API
export const projectsApi = {
  create: (data: any) =>
    api.post<ApiResponse<{ project: any }>>('/projects', data),
  
  list: (params?: { mine?: boolean; status?: string; limit?: number; offset?: number }) =>
    api.get<PaginatedResponse<any>>('/projects', { params }),
  
  get: (id: string) =>
    api.get<ApiResponse<{ project: any }>>(`/projects/${id}`),
  
  update: (id: string, data: any) =>
    api.patch<ApiResponse<{ project: any }>>(`/projects/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/projects/${id}`),
};

// Items API
export const itemsApi = {
  create: (data: any) =>
    api.post<ApiResponse<{ item: any }>>('/items', data),
  
  list: (params?: { status?: string; limit?: number; cursor?: string; project?: string; seller?: string }) =>
    api.get<PaginatedResponse<any>>('/items', { params }),
  
  get: (id: string) =>
    api.get<ApiResponse<{ item: any }>>(`/items/${id}`),
  
  update: (id: string, data: any) =>
    api.patch<ApiResponse<{ item: any }>>(`/items/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/items/${id}`),
};

// Listings API
export const listingsApi = {
  list: (params?: { 
    category?: string; 
    condition?: string; 
    minPrice?: number; 
    maxPrice?: number; 
    location?: string;
    limit?: number; 
    offset?: number; 
  }) =>
    api.get<PaginatedResponse<any>>('/listings', { params }),
  
  get: (id: string) =>
    api.get<ApiResponse<{ listing: any }>>(`/listings/${id}`),
  
  create: (data: any) =>
    api.post<ApiResponse<{ listing: any }>>('/listings', data),
  
  update: (id: string, data: any) =>
    api.patch<ApiResponse<{ listing: any }>>(`/listings/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/listings/${id}`),
  
  save: (id: string) =>
    api.post<ApiResponse>(`/listings/${id}/save`),
  
  unsave: (id: string) =>
    api.delete<ApiResponse>(`/listings/${id}/save`),
};

// Orders API
export const ordersApi = {
  list: () =>
    api.get<PaginatedResponse<any>>('/orders'),
  
  get: (id: string) =>
    api.get<ApiResponse<{ order: any }>>(`/orders/${id}`),
};

// Payments API
export const paymentsApi = {
  createIntent: (itemId: string) =>
    api.post<ApiResponse<{ clientSecret: string }>>('/payments/create-intent', { itemId }),
  
  cancelReservation: (itemId: string) =>
    api.post<ApiResponse>('/payments/cancel-reservation', { itemId }),
  
  checkoutSuccess: (paymentIntentId: string) =>
    api.post<ApiResponse<{ order: any }>>('/payments/checkout-success', { paymentIntentId }),
};

// Uploads API
export const uploadApi = {
  uploadImages: (formData: FormData) => {
    return api.post<ApiResponse<{ 
      urls: string[];
      files: {
        filename: string;
        originalName: string;
        size: number;
        mimetype: string;
        url: string;
      }[];
    }>>('/uploads/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Images API
export const imagesApi = {
  list: () =>
    api.get<{ success: boolean; photos: any[]; count: number }>('/images'),
  
  create: (data: { filename: string; url: string; description?: string }) =>
    api.post<ApiResponse<any>>('/images', data),
  
  delete: (filename: string) =>
    api.delete<ApiResponse<{ deleted: boolean }>>(`/images/${filename}`),
  
  deleteBatch: (filenames: string[]) =>
    api.delete<ApiResponse<{ deleted: string[]; failed: string[] }>>('/images/batch', {
      data: { filenames }
    }),
  
  rescale: (filename: string, options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'jpeg' | 'png' | 'webp';
  }) =>
    api.post<ApiResponse<{ filename: string }>>(`/images/${filename}/rescale`, options),
  
  crop: (filename: string, cropArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  }, voiceAnnotation?: string) =>
    api.post<ApiResponse<{ filename: string; url: string; newFilename?: string }>>(`/images/${encodeURIComponent(filename)}/crop`, {
      x: cropArea.x,
      y: cropArea.y,
      width: cropArea.width,
      height: cropArea.height,
      ...(voiceAnnotation && { voiceAnnotation })
    }),

  saveWithItems: (data: {
    filename: string;
    imageData: string;
    voiceAnnotation?: string;
    itemPhotos?: Array<{
      imageData: string;
      detection: {
        class: string;
        confidence: number;
        boundingBox: {
          x: number;
          y: number;
          width: number;
          height: number;
        };
      };
      voiceAnnotation?: string;
    }>;
  }) =>
    api.post<ApiResponse<{ photoId: string; filename: string; itemPhotosCount: number }>>('/images/save-with-items', data),
  
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post<ApiResponse<{ filename: string; url: string }>>('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getInfo: (filename: string) =>
    api.get<ApiResponse<{
      filename: string;
      originalPath: string;
      size: number;
      dimensions: { width: number; height: number };
      format: string;
    }>>(`/images/${filename}/info`),
  
  moveToProject: (photoIds: string[], projectId: string) =>
    api.post<ApiResponse<{ updatedCount: number; projectId: string; projectTitle: string }>>('/images/move-to-project', {
      photoIds,
      projectId
    }),

  // Photo measurement and scale reference APIs
  setScaleReference: (photoId: string, data: {
    line: { x1: number; y1: number; x2: number; y2: number };
    realWorldLength: number;
    units: 'mm' | 'cm' | 'm' | 'in' | 'ft';
    referenceType: 'ruler' | 'credit_card' | 'coin' | 'custom';
    referenceObject?: string;
    imageWidth?: number;
    imageHeight?: number;
  }) =>
    api.post<ApiResponse<{
      photoId: string;
      scaleReference: any;
    }>>(`/images/${photoId}/scale-reference`, data),

  addMeasurements: (photoId: string, data: {
    measurements: Array<{
      id: string;
      label: string;
      line: { x1: number; y1: number; x2: number; y2: number };
    }>;
    targetUnits?: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  }) =>
    api.post<ApiResponse<{
      photoId: string;
      measurements: any[];
      estimatedDimensions: any;
    }>>(`/images/${photoId}/measurements`, data),

  getMeasurements: (photoId: string) =>
    api.get<ApiResponse<{
      photoId: string;
      scaleReference?: any;
      measurements: any[];
      estimatedDimensions?: any;
    }>>(`/images/${photoId}/measurements`),

  removeScaleReference: (photoId: string) =>
    api.delete<ApiResponse>(`/images/${photoId}/scale-reference`),
};

// Admin API
export const adminApi = {
  fixAccountRoles: () =>
    api.post<ApiResponse<{ updates: any[]; totalFixed: number }>>('/admin/fix-account-roles'),
};

// AI API
export const aiApi = {
  estimate: async (data: { 
    imageUrl?: string; 
    base64Image?: string; 
    title?: string; 
    description?: string;
    condition?: 'new' | 'used' | 'salvage';
    materials?: string[];
    dimensions?: {
      width: number;
      height: number;
      depth: number;
      units: string;
    };
    customPrompt?: string;
    detectMultipleItems?: boolean;
  }) => {
    const startTime = Date.now();
    
    // Log the request
    MCPDebugLogger.log('openai_request', {
      endpoint: '/ai/estimate',
      payload: {
        ...data,
        imageUrl: data.imageUrl ? `${data.imageUrl.substring(0, 50)}...` : undefined,
        base64Image: data.base64Image ? `[base64 data: ${data.base64Image.length} chars]` : undefined,
      },
      message: 'OpenAI Image Analysis Request'
    });

    try {
      const response = await api.post<ApiResponse<{
        estimatedValue: number;
        confidence: number;
        notes: string;
        recommendedTitle: string;
        attributes: string[];
        reasoning: string;
      }>>('/ai/estimate', data);

      const duration = Date.now() - startTime;
      
      // Log the response
      MCPDebugLogger.log('openai_response', {
        endpoint: '/ai/estimate',
        response: response.data,
        message: 'OpenAI Image Analysis Response'
      }, duration);

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      MCPDebugLogger.logError('/ai/estimate', error);
      throw error;
    }
  },

  chat: async (message: string, context?: string) => {
    const startTime = Date.now();
    
    MCPDebugLogger.log('openai_request', {
      endpoint: '/ai/chat',
      payload: { message: message.substring(0, 100) + '...', context },
      message: 'OpenAI Chat Request'
    });

    try {
      const response = await api.post<ApiResponse<{
        response: string;
        context: string;
      }>>('/ai/chat', { message, context });

      const duration = Date.now() - startTime;
      MCPDebugLogger.log('openai_response', {
        endpoint: '/ai/chat',
        response: response.data,
        message: 'OpenAI Chat Response'
      }, duration);

      return response;
    } catch (error) {
      MCPDebugLogger.logError('/ai/chat', error);
      throw error;
    }
  },

  generateImages: async (count?: number) => {
    const startTime = Date.now();
    
    MCPDebugLogger.log('openai_request', {
      endpoint: '/ai/generate-images',
      payload: { count },
      message: 'OpenAI Image Generation Request'
    });

    try {
      const response = await api.post<ApiResponse<{
        images: string[];
        count: number;
      }>>('/ai/generate-images', { count });

      const duration = Date.now() - startTime;
      MCPDebugLogger.log('openai_response', {
        endpoint: '/ai/generate-images',
        response: {
          ...response.data,
          data: response.data.data ? {
            ...response.data.data,
            images: response.data.data.images?.map((img, i) => `Image ${i + 1}: ${img.substring(0, 50)}...`)
          } : undefined
        },
        message: 'OpenAI Image Generation Response'
      }, duration);

      return response;
    } catch (error) {
      MCPDebugLogger.logError('/ai/generate-images', error);
      throw error;
    }
  },

  removeBackground: async (data: { imageUrl: string }) => {
    const startTime = Date.now();
    
    MCPDebugLogger.log('openai_request', {
      endpoint: '/ai/remove-background',
      payload: {
        imageUrl: data.imageUrl.substring(0, 50) + '...'
      },
      message: 'OpenAI Background Removal Request'
    });

    try {
      const response = await api.post<ApiResponse<{
        processedImageUrl: string;
        originalImageUrl: string;
      }>>('/ai/remove-background', data);

      const duration = Date.now() - startTime;
      MCPDebugLogger.log('openai_response', {
        endpoint: '/ai/remove-background',
        response: {
          ...response.data,
          data: response.data.data ? {
            ...response.data.data,
            processedImageUrl: response.data.data.processedImageUrl.substring(0, 50) + '...',
            originalImageUrl: response.data.data.originalImageUrl.substring(0, 50) + '...'
          } : undefined
        },
        message: 'OpenAI Background Removal Response'
      }, duration);

      return response;
    } catch (error) {
      MCPDebugLogger.logError('/ai/remove-background', error);
      throw error;
    }
  },

  reprocessItem: async (data: {
    itemId: string;
    additionalContext: string;
    imageUrl?: string;
    currentTitle?: string;
    currentDescription?: string;
    condition?: 'new' | 'used' | 'salvage';
    materials?: string[];
    dimensions?: {
      width: number;
      height: number;
      depth: number;
      units: string;
    };
  }) => {
    const startTime = Date.now();
    
    MCPDebugLogger.log('openai_request', {
      endpoint: '/ai/reprocess-item',
      payload: {
        ...data,
        imageUrl: data.imageUrl ? data.imageUrl.substring(0, 50) + '...' : undefined,
        additionalContext: data.additionalContext.substring(0, 100) + '...'
      },
      message: 'OpenAI Item Reprocessing Request'
    });

    try {
      const response = await api.post<ApiResponse<{
        estimatedValue: number;
        confidence: number;
        notes: string;
        recommendedTitle: string;
        attributes: string[];
        reasoning: string;
      }>>('/ai/reprocess-item', data);

      const duration = Date.now() - startTime;
      MCPDebugLogger.log('openai_response', {
        endpoint: '/ai/reprocess-item',
        response: response.data,
        message: 'OpenAI Item Reprocessing Response'
      }, duration);

      return response;
    } catch (error) {
      MCPDebugLogger.logError('/ai/reprocess-item', error);
      throw error;
    }
  },

  saveGeneratedImages: (images: { url: string; title?: string; description?: string }[]) =>
    api.post<ApiResponse<{
      items: any[];
      count: number;
    }>>('/ai/save-generated-images', { images }),

  saveGeneratedPhotos: (images: { url: string; title?: string; description?: string }[]) =>
    api.post<ApiResponse<{
      photos: any[];
      count: number;
    }>>('/ai/save-generated-photos', { images }),

  generateTestProject: () =>
    api.post<ApiResponse<{
      title: string;
      description: string;
      address: string;
      tags: string[];
    }>>('/ai/generate-test-project'),
};

// MCP Chat API
export const mcpChatApi = {
  sendMessage: (data: {
    message: string;
    context?: string;
  }) =>
    api.post<ApiResponse<{
      response: string;
      toolsUsed?: string[];
      context: string;
      timestamp: string;
      data?: any[];
    }>>('/mcp-chat/message', data),

  getTools: () =>
    api.get<ApiResponse<{
      tools: Array<{
        name: string;
        description: string;
        category: string;
      }>;
      count: number;
    }>>('/mcp-chat/tools'),

  searchTimber: (data: {
    query?: string;
    condition?: string;
    maxPrice?: number;
    minPrice?: number;
    location?: string;
    limit?: number;
  }) =>
    api.post<ApiResponse<{
      response: string;
      toolsUsed?: string[];
      searchParams: any;
      timestamp: string;
    }>>('/mcp-chat/search-timber', data),
};

// Speech API
export const speechApi = {
  getToken: () =>
    api.post<ApiResponse<{
      token: string;
      region: string;
      expiresIn: number;
    }>>('/speech/token'),
};

// Test API (Admin only)
export const testApi = {
  generateListings: (data: {
    count: number;
    sellerId?: string;
    categories?: string[];
  }) =>
    api.post<ApiResponse<{
      count: number;
      listings: Array<{
        _id: string;
        title: string;
        price: number;
        category: string;
        condition: string;
        location: {
          city: string;
          region: string;
        };
      }>;
    }>>('/test/generate-listings', data),

  deleteTestListings: () =>
    api.delete<ApiResponse<{ deletedCount: number }>>('/test/listings'),

  deleteAllMockData: () =>
    api.delete<ApiResponse<{
      listings: number;
      items: number;
      projects: number;
      orders: number;
      testCollections: number;
    }>>('/test/all-mock-data', { timeout: 60000 }), // 60 second timeout

  getTestListingsCount: () =>
    api.get<ApiResponse<{ testListingsCount: number }>>('/test/listings/count'),
};

export default api;
